/**
 * AI cost safeguards (approved by the owner on 2026-11-03). The database decides (public.ai_guard_eval,
 * ai_free_calls_left, ai_allowance_of, reserve_type_fill, buy_ai_credit in 20261212000000_ai_guard.sql);
 * these mirror its defaults and rules for the app's texts and the operator pages. A database test keeps
 * AI_GUARD_DEFAULTS equal to the migration's settings.
 */
import { addDays, addMonths } from "./reminders";
import type { Plan } from "./features";
import { periodCharge, type Pricing, type SubscriptionState } from "./subscription";

export interface AiGuardSettings {
  /** Real AI cost of all workshops together per Budapest day; at it new AI requests pause until tomorrow. */
  dailyCostCapHuf: number;
  /** Real AI cost of one workshop per day (the operator may set another per workshop). */
  workshopDailyCapHuf: number;
  /** Free everyday calls (photo reading, dictation, company lookup) per month, by the effective plan. */
  freeCalls: Record<Plan, number>;
  /** During the trial: monthly AI credit instead of the plan's... */
  trialAllowance: number;
  /** ...free everyday calls for the whole trial... */
  trialFreeCalls: number;
  /** ...and shared data sheet fills for the whole trial. */
  trialSheetFills: number;
  rateLimitPerMinute: number;
  rateLimitPerDay: number;
  /** Extra credit a workshop may buy per calendar month without the operator. */
  monthlyPurchaseLimitHuf: number;
}

export const AI_GUARD_DEFAULTS: AiGuardSettings = {
  dailyCostCapHuf: 5000,
  workshopDailyCapHuf: 1500,
  freeCalls: { basic: 100, medium: 200, premium: 300 },
  trialAllowance: 2000,
  trialFreeCalls: 50,
  trialSheetFills: 5,
  rateLimitPerMinute: 10,
  rateLimitPerDay: 200,
  monthlyPurchaseLimitHuf: 10000,
};

/** Error codes the AI guard and the purchase answer with (texts in every locale). */
export const AI_GUARD_ERRORS = ["AI_PAUSED", "AI_DAILY_LIMIT", "AI_RATE_LIMIT", "AI_CREDIT_LOW", "AI_SUBSCRIPTION_BLOCKED", "AI_PURCHASE_TRIAL", "AI_PURCHASE_LIMIT"] as const;

/** Free everyday calls: the trial's number (for the whole trial) or the effective plan's (per month). */
export function freeCallsFor(s: AiGuardSettings, plan: Plan, trial: boolean): number {
  return trial ? s.trialFreeCalls : s.freeCalls[plan];
}

/** Monthly AI credit: the operator's own figure, in the trial the trial allowance, else the plan's. */
export function allowanceFor(s: AiGuardSettings, planAllowance: Record<Plan, number>, plan: Plan, trial: boolean, own: number | null = null): number {
  return own ?? (trial ? s.trialAllowance : planAllowance[plan]);
}

/** Shared data sheet fills: the trial's number for the whole trial, else sheetFillsPerMonth. */
export function sheetFillsFor(s: AiGuardSettings, sheetFillsPerMonth: number, trial: boolean): number {
  return trial ? s.trialSheetFills : sheetFillsPerMonth;
}

export interface PurchaseState {
  trial: boolean;
  readOnly: boolean;
  /** workshop_billing.status */
  status: string;
  boughtThisMonth: number;
  /** The operator's own monthly limit, else monthlyPurchaseLimitHuf. */
  limit: number;
}

/** Why a pack cannot be bought now (same order as public.buy_ai_credit), or null. */
export function purchaseRefusal(p: PurchaseState, amount: number): "READ_ONLY" | "AI_SUBSCRIPTION_BLOCKED" | "AI_PURCHASE_TRIAL" | "AI_PURCHASE_LIMIT" | null {
  if (p.readOnly) return "READ_ONLY";
  if (p.status === "overdue" || p.status === "suspended") return "AI_SUBSCRIPTION_BLOCKED";
  if (p.trial) return "AI_PURCHASE_TRIAL";
  if (p.boughtThisMonth + amount > p.limit) return "AI_PURCHASE_LIMIT";
  return null;
}

/** No AI at all for a read only, overdue or suspended workshop (same as public.ai_blocked). */
export function aiBlocked(readOnly: boolean, status: string): "READ_ONLY" | "AI_SUBSCRIPTION_BLOCKED" | null {
  if (readOnly) return "READ_ONLY";
  return status === "overdue" || status === "suspended" ? "AI_SUBSCRIPTION_BLOCKED" : null;
}

/** The exchange rate should be checked when it was last changed more than this many days ago. */
export const USD_HUF_STALE_DAYS = 30;

/** Whether the exchange rate is older than USD_HUF_STALE_DAYS (or its date is unknown). */
export function usdHufStale(changedAt: string | null | undefined, today: string): boolean {
  if (!changedAt) return true;
  return addDays(changedAt.slice(0, 10), USD_HUF_STALE_DAYS) < today;
}

/**
 * The subscription's net fee for one calendar month, spread evenly (a yearly period counts a twelfth):
 * 0 for "free", during a trial without a chosen plan, and for a month the trial covers to its end.
 */
export function subscriptionMonthNet(s: SubscriptionState, pricing: Pricing, month: string): number {
  if (s.status === "free") return 0;
  const first = `${month.slice(0, 7)}-01`;
  const last = addDays(addMonths(first, 1), -1);
  if (s.trialUntil && s.trialUntil >= last) return 0;
  if (s.trialUntil && !s.planAfterTrial && s.status === "trial") return 0;
  const c = periodCharge(s, pricing, first);
  return Math.round(c.net / (s.period === "yearly" ? 12 : 1));
}

/** Real AI cost as a share of the month's fee; flagged above alertShare, or when there is cost but no fee. */
export function aiCostShare(costHuf: number, feeNet: number, alertShare: number): { share: number | null; flagged: boolean } {
  if (costHuf <= 0) return { share: feeNet > 0 ? 0 : null, flagged: false };
  if (feeNet <= 0) return { share: null, flagged: true };
  const share = costHuf / feeNet;
  return { share, flagged: share > alertShare };
}
